/* eslint-disable max-len */
import { MutableRecordStore, VoidCallback } from '@deqo/foundation';
import React, { useCallback, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { useInterval } from 'react-use';
import styled, { css, keyframes } from 'styled-components';
import { useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher, CommandInvoker } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { Cell, Colors, Float, Grid, toRem, usePrompt } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { BetterPopArrow } from '../../../ui/PopArrow';
import { useResolve } from '../../Container';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { PopperInner, PopperMount } from '../ui/PopperElements';
import { useComponentObserver } from '../visualization/ComponentObserver';
import { Meeting } from './Meeting';
import { meetingIncrementTimer, meetingPauseTimer, meetingResetTimer, meetingSetTimer, meetingStartTimer } from './MeetingCommands';
import { useActiveMeeting } from './MeetingHooks';
import { resolveActiveMeeting } from './MeetingUtil';

class MeetingTimerStore extends MutableRecordStore({ alarm: 0, value: '00:00' }) { }

const useMeetingTimerStore = (meeting: Meeting) => {
    const [store] = useState(new MeetingTimerStore());
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onTick = useCallback(() => {
        if (!meeting?.alive) return;
        const next = meeting.getTimeReadingFormatted();
        if (next !== store.value) {
            const alarm = next === '00:00' && !!meeting.timeStarted ? 1 : 0;
            store.mutate({
                alarm: store.alarm + alarm,
                value: meeting.getTimeReadingFormatted(),
            });
        }
    }, [meeting]);
    useInterval(onTick, 200);
    const state = useComponentObserver(meeting);
    useEffect(() => onTick(), [state]);
    return store;
};

const TimerFrame = styled.div`
    width: 5rem;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.25rem;
    font-weight: 450;
    font-family: 'Inter';
    font-variant-numeric: tabular-nums;
`;

const TimerInput = styled.input`
    width: 5rem;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 450;
    font-family: 'Inter';
    font-variant-numeric: tabular-nums;
`;

const TimerEditConfirmButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;    
    transform: translateX(100%);
`;

// eslint-disable-next-line react/display-name
const TimerEdit = React.memo<{ onComplete: VoidCallback }>(({ onComplete }) => {
    const dispatch = useResolve(CommandDispatcher);
    const meeting = resolveActiveMeeting(useResolve(ObjectManager));
    const [input, setInput] = useState(null as HTMLInputElement);
    const [value, setValue] = useState(meeting?.getTimeReadingFormatted() ?? '');

    const onChange = (e: any) => {
        const v = e.target.value;
        setValue(v.replace(/[^0-9:\s\.]/g, ''));
    };

    const onSave = useCallback(() => {
        const [m, s] = value.split(/[:\s\.]/g, 2).map(x => parseInt(x) || 0);
        const ms = Math.min(359940000, ((m || 0) * 60 * 1000) + ((s || 0) * 1000));
        dispatch(meetingSetTimer, [ms]);
        onComplete();
    }, [value, onComplete]);

    const onInputKey = useCallback((e: any) => {
        if (e.key === 'Enter') {
            onSave();
            onComplete();
        }
        else if (e.key === 'Escape') {
            onComplete();
        }
    }, [onSave, onComplete]);

    useEffect(() => {
        input?.focus();
        input?.select();
    }, [input]);

    return <Flexer>
        <TimerInput ref={setInput} value={value} onChange={onChange} onKeyDown={onInputKey} onBlur={onSave} />
    </Flexer>;
});

const Label = styled.span`
    font-weight: 450;
`;

const QuickAction = ({ label, invoker }: { label: string, invoker: CommandInvoker }) => ({ label, invoker });
type QuickAction = ReturnType<typeof QuickAction>;

const quickActions = [
    QuickAction({ label: '+1\u2032', invoker: d => d(meetingIncrementTimer, [1 * 60 * 1000]) }),
    QuickAction({ label: '+5\u2032', invoker: d => d(meetingIncrementTimer, [5 * 60 * 1000]) }),
    QuickAction({ label: '+10\u2032', invoker: d => d(meetingIncrementTimer, [10 * 60 * 1000]) }),
    QuickAction({ label: '+15\u2032', invoker: d => d(meetingIncrementTimer, [15 * 60 * 1000]) }),
];

const TimerControlView = () => {

    const dispatch = useResolve(CommandDispatcher);
    const objects = useResolve(ObjectManager);
    const meeting = resolveActiveMeeting(objects);
    useComponentObserver(meeting);

    const onQuickAction = (qa: QuickAction) => void (qa.invoker(dispatch));
    const onStart = () => dispatch(meetingStartTimer);
    const onPause = () => dispatch(meetingPauseTimer);
    const onReset = () => dispatch(meetingResetTimer);

    return <Flexer width="14" padding=".5">
        <Grid columns="1fr 1fr 1fr 1fr" columnGap=".125rem" rowGap=".125rem">
            {!meeting.timeStarted && <Cell width={2}>
                <DropButton data-action="editor.timer.start" overflow="0" padding=".375" onClick={onStart}>
                    <Icon size="1" src="deqo://icons/deqo/timer-play" />
                    <Label>Play</Label>
                </DropButton>
            </Cell>}
            {!!meeting.timeStarted && <Cell width={2}>
                <DropButton data-action="editor.timer.pause" overflow="0" padding=".375" onClick={onPause}>
                    <Icon size="1" src="deqo://icons/deqo/timer-pause" />
                    <Label>Pause</Label>
                </DropButton>
            </Cell>}
            <Cell width={2}>
                <DropButton data-action="editor.timer.reset" overflow="0" padding=".375" onClick={onReset}>
                    <Icon size="1" src="deqo://icons/deqo/timer-reset" />
                    <Label>Reset</Label>
                </DropButton>
            </Cell>
            {quickActions.map((qa) =>
                <Cell key={qa.label}>
                    <DropButton data-action={`editor.timer.add${parseInt(qa.label)}`} overflow="0" padding=".375" onClick={() => onQuickAction(qa)}>
                        <Label>{qa.label}</Label>
                    </DropButton>
                </Cell>
            )}
        </Grid>
    </Flexer>;
};

const AlarmLeft = styled.div`
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    transform: translateX(-100%);
    pointer-events: none;

    svg {
        transform: rotate(180deg);
    }
`;

const AlarmRight = styled.div`
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    transform: translateX(100%);
    pointer-events: none;
`;

const flashAnim = keyframes`
    0%, 49% { 
        background: #fff;
        color: ${Colors.primaryText};    
        border-radius: 0;
    }
    50%, 99% { 
        background: ${Colors.primaryText};
        color: #fff;  
        border-radius: .375rem;
     }
`;

const TimerButtonFrame = styled.div<{ flashing?: boolean }>`
    position: relative;
    color: ${Colors.primaryText};

    ${p => p.flashing && css`
        animation: ${flashAnim} 2s 5;
    `};
`;

const TimerButton = React.memo<{ open: boolean }>(({ open }) => {

    const meeting = useActiveMeeting();
    const store = useObserverReturningStore(useMeetingTimerStore(meeting));
    const [editing, setEditing] = useState(false);

    const onClick = useCallback(() => {
        if (!!open) {
            setEditing(true);
        }
        store.mutate({ alarm: 0 });
    }, [open, store]);

    const cursorStyle = { cursor: open ? 'text' : '' };
    const alarm = store.alarm > 0;

    return <TimerButtonFrame flashing={!open && !editing && alarm} onClick={onClick}>
        {/* <AlarmLeft>
            <Alarm size="2.5" active={alarm} />
        </AlarmLeft> */}
        <DropButton active={open} overflow="0" padding=".25 1" style={cursorStyle}>
            <DropButton.Border color={Colors.primaryText} />
            {!editing && <TimerFrame>{store.value}</TimerFrame>}
            {editing && <TimerEdit onComplete={() => setEditing(false)} />}
        </DropButton>
        {/* <AlarmRight>
            <Alarm size="2.5" active={alarm} />
        </AlarmRight> */}
    </TimerButtonFrame>;
});

const TimerPopper = React.memo(() => {

    const [open, setOpen] = useState(false);
    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom',
    });

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction: () => setOpen(false),
    });

    const onClick = useCallback(() => {
        if (!open) {
            setOpen(true);
        }
    }, [open]);

    return <Flexer ref={promptRef} flex center color={Colors.primaryText}>
        <Flexer ref={setRefElmt} data-action={!open ? 'editor.timer.open' : 'editor.timer.edit'} onClick={onClick}>
            <TimerButton open={open} />
        </Flexer>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <PopperInner direction="down" offset=".5">
                <Float>
                    <TimerControlView />
                </Float>
                <BetterPopArrow placement="bottom" color="#fff" border={[1, Colors.border]} style={{ left: '50%', top: '.375rem' }} />
            </PopperInner>
        </PopperMount>}
    </Flexer>;
});

const TimerRevealTransform = styled.div``;

const TimerReveal = styled.div<{ show: boolean }>`
    position: relative;
    width: ${p => toRem(p.show ? 6 : 0)};
    transition: ${p => p.show ? '' : '.1s'} width .25s cubic-bezier(0,.76,.47,1);

    ${TimerRevealTransform} {
        opacity: 0;
        transform: scale(.8);
        transition: 
            ${p => p.show ? '.1s' : ''} transform .25s cubic-bezier(0,.76,.47,1),
            ${p => p.show ? '.1s' : ''} opacity .25s linear;

        ${p => p.show && css`
            opacity: 1;
            transform: scale(1);
        `}
    }
`;

export const MeetingTimerView = React.memo(() => {
    const meeting = useActiveMeeting();
    if (!meeting) return null;

    return <TimerReveal show={!!meeting.timer}>
        <TimerRevealTransform>
            <TimerPopper />
        </TimerRevealTransform>
    </TimerReveal>;
});
